import type { CustomCssConfig, CustomCssConfigKey, CustomCssContainerConfig, CustomCssInputConfig, CustomCssTextConfig, CustomCssVariables } from 'widget-components/types'
import { type ExtendedBlock } from 'widget-components/integrations/CustomPage/types'
import { omit } from 'ramda'
import useCustomCss from 'widget-components/composables/custom-css'

export const BLOCK_CUSTOM_CSS_KEY_MAP: Record<string, CustomCssConfigKey> = {
    ACTION_BUTTON: 'container',
    DISPLAY_DOCUMENT: 'container',
    DIVIDER: 'container',
    CONTAINER: 'container',
    INPUT_CHECKBOX_LIST: 'input',
    INPUT_CHECKBOX: 'input',
    INPUT_CONSENT_LIST: 'input',
    INPUT_COUNTRY: 'input',
    INPUT_DATE_TIME: 'input',
    INPUT_DATE: 'input',
    INPUT_DIGIT: 'input',
    INPUT_DOCUMENT: 'input',
    INPUT_FILE: 'input',
    INPUT_DROPDOWN: 'input',
    INPUT_EMAIL: 'input',
    INPUT_NUMBER: 'input',
    INPUT_PHONE_NUMBER: 'input',
    INPUT_RADIO: 'input',
    INPUT_SLIDER: 'input',
    INPUT_TEXT_AREA: 'input',
    INPUT_TEXT: 'input',
    PROGRESS_BAR: 'container',
    SELECTION_LIST: 'container',
    STEPPER: 'container',
    TEXT_COPIABLE: 'container',
    TEXT_PLAIN: 'text',
    TEXT_TITLE: 'text',
    TOGGLE: 'input',
    NAVIGATION: 'container',
}

const FONT_FALLBACK_STACK = "'Helvetica', 'Arial', sans-serif"
const FONT_FAMILY_VARIABLE_NAMES = ['--font-family-primary', '--font-family-secondary']

function applyCssVariables(
    cssVars: CustomCssVariables,
    element?: HTMLElement,
) {
    const elem = element || document.documentElement

    const cssVariables = Object.entries(cssVars || {})

    if (cssVariables.length) {
        for (const [cssName, cssValue] of cssVariables) {
            let value = cssValue

            if (cssName === '--tl-widget-background-url') {
                value = value.startsWith('url') ? value : `url(${value})`
            }

            if (FONT_FAMILY_VARIABLE_NAMES.includes(cssName) && !value.includes(',')) {
                value = `${value}, ${FONT_FALLBACK_STACK}`
            }

            elem.style.setProperty(cssName, value)
        }
    }
}

const mapConfigSectionToCssVariables = <T extends object>(
    configSection: T | undefined,
    mapping: Record<keyof T, string>,
): CustomCssVariables => {
    const result: CustomCssVariables = {}
    if (!configSection) return result;

    (Object.keys(mapping) as Array<keyof T>).forEach((key) => {
        const value = configSection[key]
        if (value !== undefined && value !== null) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (result as any)[mapping[key]] = value
        }
    })
    return result
}

const clearCssVariables = (element: HTMLElement) => {
    for (let i = element.style.length - 1; i >= 0; i--) {
        const name = element.style[i]
        if (name.startsWith('--tl')) {
            element.style.removeProperty(name)
        }
    }
}

const getCssVariables = (config: CustomCssConfig): CustomCssVariables => {
    const { isCustomCssConfigFlat } = useCustomCss()

    const cssVariables: CustomCssVariables = {}

    if (isCustomCssConfigFlat(config)) {
        return config as CustomCssVariables
    }

    if (!config) {
        return cssVariables
    }

    // Root level
    if ('primaryColor' in config && config.primaryColor) {
        cssVariables['--tl-wc-primary-color'] = config.primaryColor
    }
    if ('secondaryColor' in config && config.secondaryColor) {
        cssVariables['--tl-wc-secondary-color'] = config.secondaryColor
    }

    // Widget background
    Object.assign(
        cssVariables,
        mapConfigSectionToCssVariables(config.background as CustomCssConfig['background'], {
            color: '--tl-widget-background-color',
            url: '--tl-widget-background-url',
        }),
    )

    // Fonts
    Object.assign(
        cssVariables,
        mapConfigSectionToCssVariables(config.font, {
            size: '--tl-font-size',
            familyPrimary: '--font-family-primary',
            familySecondary: '--font-family-secondary',
            cssUrl: '--tl-font-css-url',
        }),
    )

    // Texts
    Object.assign(
        cssVariables,
        mapConfigSectionToCssVariables(config.text, {
            primaryColor: '--tl-text-primary-color',
            secondaryColor: '--tl-text-secondary-color',
            linkColor: '--tl-text-link-color',
        }),
    )

    // Inputs
    Object.assign(
        cssVariables,
        mapConfigSectionToCssVariables(config.input, {
            height: '--tl-inputs-height',
            focusColor: '--tl-inputs-focus-color',
            focusForegroundColor: '--tl-inputs-focus-fgcolor',
            borderColor: '--tl-inputs-border-color',
            backgroundColor: '--tl-inputs-bg-color',
            borderRadius: '--tl-inputs-border-radius',
            textPrimaryColor: '--tl-inputs-text-primary-color',
            disabledBgColor: '--tl-inputs-disabled-bgcolor',
            linkColor: '--tl-text-link-color',
        }),
    )

    // Containers
    Object.assign(
        cssVariables,
        mapConfigSectionToCssVariables(config.container, {
            height: '--tl-container-height',
            primaryColor: '--tl-container-primary-color',
            disabledColor: '--tl-container-disabled-color',
            borderRadius: '--tl-container-border-radius',
            borderColor: '--tl-container-border-color',
            borderSize: '--tl-container-border-size',
            backgroundColor: '--tl-container-bg-color',
            buttonColor: '--tl-container-bttn-color',
            buttonBackgroundColor: '--tl-container-bttn-bg-color',
            buttonSecondaryColor: '--tl-container-bttn-secondary-color',
            buttonSecondaryBackgroundColor: '--tl-container-bttn-secondary-bg-color',
            buttonSecondaryBorderColor: '--tl-container-bttn-secondary-border-color',
            textPrimaryColor: '--tl-container-text-primary-color',
            textSecondaryColor: '--tl-container-text-secondary-color',
            itemGap: '--tl-container-item-gap',
            activeItemPadding: '--tl-container-active-item-padding',
            activeItemBackgroundColor: '--tl-container-active-item-bg-color',
            boxShadow: '--tl-container-box-shadow',
            padding: '--tl-container-padding',
            borderLeft: '--tl-container-border-left',
            borderTop: '--tl-container-border-top',
            borderRight: '--tl-container-border-right',
            borderBottom: '--tl-container-border-bottom',
        }),
    )

    // prefixed
    Object.entries(config).forEach(([key, value]) => {
        if (key.startsWith('--tl-')) {
            Object.assign(cssVariables, { [key]: value })
        }
    })

    return cssVariables
}

const getContainerCssVariables = (config: CustomCssContainerConfig): CustomCssVariables => {
    if (!config) return {}

    const baseConfig: CustomCssConfig = {
        font: { size: config.fontSize },
        container: omit(['fontSize'], config),
    }
    return {
        ...getCssVariables(baseConfig),
        ...config.fontWeight ? { '--tl-font-weight': config.fontWeight } : {},
    }
}

const getInputCssVariables = (config: CustomCssInputConfig): CustomCssVariables => {
    if (!config) return {}

    const baseConfig: CustomCssConfig = {
        font: { size: config.fontSize },
        input: omit(['fontSize'], config),
    }
    return {
        ...getCssVariables(baseConfig),
        ...config.fontWeight ? { '--tl-font-weight': config.fontWeight } : {},
    }
}

const getTextCssVariables = (config: CustomCssTextConfig): CustomCssVariables => {
    if (!config) return {}

    const baseConfig: CustomCssConfig = {
        font: { size: config.fontSize },
        text: omit(['fontSize'], config),
    }
    return {
        ...getCssVariables(baseConfig),
        ...config.fontWeight ? { '--tl-font-weight': config.fontWeight } : {},
    }
}

const getBlockCssVariables = (block: ExtendedBlock) => {
    if (isCustomCssConfigFlat(block.style as CustomCssConfig)) {
        return block.style as CustomCssVariables
    }

    const fns = {
        container: getContainerCssVariables,
        input: getInputCssVariables,
        text: getTextCssVariables,
    }
    const configKey = (BLOCK_CUSTOM_CSS_KEY_MAP[block.type] || 'global') as keyof typeof fns
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return fns[configKey]?.(block.style as any) || getCssVariables(block.style as any)
}

const FONT_ELEMENT_ID = 'custom-font-css'

const FONT_FILE_FORMATS: Record<string, string> = {
    woff2: 'woff2',
    woff: 'woff',
    ttf: 'truetype',
    otf: 'opentype',
}

const getFontFileFormat = (url: string): string | undefined => {
    const extension = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase()
    return extension ? FONT_FILE_FORMATS[extension] : undefined
}

const getFontFamilyName = (opts: {config?: CustomCssConfig, variables?: CustomCssVariables }): string => {
    const family = opts.variables?.['--font-family-primary'] || opts.config?.font?.familyPrimary || ''
    return family.split(',')[0].trim().replace(/^['"]|['"]$/g, '')
}

const createFontFaceStyle = (url: string, format: string, family: string): HTMLStyleElement => {
    const el = document.createElement('style')
    // a single file has to cover every weight the widget asks for, otherwise anything
    // bolder than normal falls back to the fallback stack
    el.textContent = `@font-face {
    font-family: ${JSON.stringify(family)};
    src: url(${JSON.stringify(url)}) format(${JSON.stringify(format)});
    font-weight: 100 900;
    font-style: normal;
    font-display: swap;
}`
    return el
}

const createStylesheetLink = (url: string): HTMLLinkElement => {
    const el = document.createElement('link')
    el.rel = 'stylesheet'
    el.href = url
    return el
}

const appendFont = (opts: {config?: CustomCssConfig, variables?: CustomCssVariables }) => {
    const { variables, config: cssConfig } = opts

    const url = Object.keys(variables || {}).length && variables!['--tl-font-css-url']
        ? variables!['--tl-font-css-url']
        : cssConfig?.font?.cssUrl || ''

    if (!url) {
        return
    }

    const format = getFontFileFormat(url)
    const family = format ? getFontFamilyName(opts) : ''

    if (format && !family) {
        // eslint-disable-next-line no-console
        console.warn(`[custom-css] font file "${url}" requires a font family to be configured`)
        return
    }

    document.getElementById(FONT_ELEMENT_ID)?.remove()

    const el = format
        ? createFontFaceStyle(url, format, family)
        : createStylesheetLink(url)

    el.id = FONT_ELEMENT_ID
    document.head.appendChild(el)
}

const isCustomCssConfigFlat = (config: CustomCssConfig | CustomCssVariables): config is CustomCssVariables =>
    Object.keys(config || {}).every(k => (k as string).startsWith('--tl-'))

export default () => ({
    applyCssVariables,
    appendFont,
    clearCssVariables,
    getCssVariables,
    getBlockCssVariables,
    getTextCssVariables,
    getInputCssVariables,
    getContainerCssVariables,
    isCustomCssConfigFlat,
})
